import { useMemo, useState } from "react";
import { BookOpenText, ChevronDown } from "lucide-react";
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { toolbarTier } from "@/partials/document-renderer/components/document-toolbar/documentToolbarTiers";
import useDocumentLink from "@/hooks/useDocumentLink";
import { useOpenAppNavigation } from "@/hooks/useOpenAppNavigation";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import {
  selectGuidanceLinks,
  type GuidanceLink,
} from "@/utils/legislationGuidanceLinks";

type GuidanceLinkListProps = {
  items: GuidanceLink[];
  onSelect: (item: GuidanceLink, event: React.MouseEvent) => void;
};

/**
 * The guidance documents, as the popover lists them. Kept free of Radix and of
 * data fetching so the list itself stays straightforward to test.
 */
export const GuidanceLinkList: React.FC<GuidanceLinkListProps> = ({
  items,
  onSelect,
}) => (
  <ul className="max-h-80 overflow-y-auto py-1">
    {items.map((item) => (
      <li key={item.documentId}>
        <button
          type="button"
          className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={(event) => onSelect(item, event)}
        >
          <span className="flex w-full items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">
              {item.label}
            </span>
            {item.isHistorical && (
              <Badge variant="secondary" className="shrink-0 text-xs">
                Historisk
              </Badge>
            )}
          </span>
          {item.title && (
            <span className="line-clamp-2 text-xs text-muted-foreground">
              {item.title}
            </span>
          )}
        </button>
      </li>
    ))}
  </ul>
);

type LegislationGuidanceLinksButtonProps = {
  /**
   * External id of the open legislation document. Left unset while the document
   * is still being processed, which is when its links are not ready either.
   */
  documentId?: string;
};

/**
 * One-click access from a rule document to the guidance that belongs to it
 * (LAR-425). The relation already exists as Retsinformation document links; it
 * was previously only reachable by opening the references sidebar and finding
 * the right group, which is what users asked us to shorten.
 *
 * Renders nothing unless the open document actually has related guidance.
 */
const LegislationGuidanceLinksButton: React.FC<
  LegislationGuidanceLinksButtonProps
> = ({ documentId }) => {
  const { openNavigation, navigationContext } = useOpenAppNavigation();
  const [open, setOpen] = useState(false);

  const { useQueryByDocumentId } = useDocumentLink();
  const { data: documentLinks } = useQueryByDocumentId(documentId ?? "", {
    enabled: Boolean(documentId),
  });

  const guidance = useMemo(
    () => selectGuidanceLinks(documentLinks, documentId),
    [documentLinks, documentId]
  );

  if (guidance.length === 0) return null;

  const handleOpenGuidance = (item: GuidanceLink, event?: React.MouseEvent) => {
    setOpen(false);
    try {
      const opened = openNavigation(
        {
          kind: "documentLink",
          link: item.link,
          currentDocumentId: documentId,
          context: navigationContext,
        },
        event
      );
      if (!opened) {
        logger.error("Could not resolve guidance document navigation target", {
          operation: "openGuidanceDocument",
          documentId,
          guidanceDocumentId: item.documentId,
        });
        showErrorToast("Kunne ikke åbne vejledningen");
      }
    } catch (error) {
      logger.error("Failed to navigate to guidance document", {
        operation: "openGuidanceDocument",
        error,
        documentId,
        guidanceDocumentId: item.documentId,
      });
      showErrorToast("Kunne ikke åbne vejledningen");
    }
  };

  // A lone historical guidance is not opened blind: it goes through the list,
  // where the "Historisk" badge says what the user is about to open.
  const single =
    guidance.length === 1 && !guidance[0].isHistorical ? guidance[0] : null;

  if (single) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 shrink-0 gap-1 px-2"
            id="legislation-guidance-trigger"
            aria-label={`Åbn vejledning: ${single.label}`}
            onClick={(event) => handleOpenGuidance(single, event)}
          >
            <BookOpenText className="size-3.5" />
            <span className={toolbarTier.fromLabelsInline}>Vejledning</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {single.title ? `${single.label} — ${single.title}` : single.label}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 shrink-0 gap-1 px-2"
          id="legislation-guidance-trigger"
          aria-label={`Vis ${guidance.length} ${
            guidance.length === 1 ? "vejledning" : "vejledninger"
          } til dokumentet`}
        >
          <BookOpenText className="size-3.5" />
          <span className={toolbarTier.fromLabelsInline}>
            {guidance.length === 1 ? "Vejledning" : "Vejledninger"}
          </span>
          <Badge variant="secondary" className="px-1 py-0 text-[10px]">
            {guidance.length}
          </Badge>
          <ChevronDown className="size-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <p className="border-b px-3 py-2 text-xs font-medium text-muted-foreground">
          Vejledninger til dokumentet
        </p>
        <GuidanceLinkList items={guidance} onSelect={handleOpenGuidance} />
      </PopoverContent>
    </Popover>
  );
};

export default LegislationGuidanceLinksButton;
